/**
 * Gerencia buscas recentes no cookie e busca preditiva da Shopify.
 * Inclui suporte a dropdown e overlay de busca full-screen.
 */

const COOKIE_KEY = 'urbo_recent_searches';
const MAX_RECENT = 8;
const COOKIE_DAYS = 30;

function setCookie(name, value, days) {
  const expires = new Date(Date.now() + days * 864e5).toUTCString();
  document.cookie = `${name}=${encodeURIComponent(value)}; expires=${expires}; path=/; SameSite=Lax`;
}

function getCookie(name) {
  const match = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'));
  return match ? decodeURIComponent(match[2]) : null;
}

function getRecentSearches() {
  try {
    const raw = getCookie(COOKIE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

function saveRecentSearches(searches) {
  setCookie(COOKIE_KEY, JSON.stringify(searches.slice(0, MAX_RECENT)), COOKIE_DAYS);
}

function addRecentSearch(term) {
  const clean = term.trim();
  if (!clean) return;

  let searches = getRecentSearches();
  searches = searches.filter(s => s.toLowerCase() !== clean.toLowerCase());
  searches.unshift(clean);
  saveRecentSearches(searches);
}

function escapeHtml(str) {
  const div = document.createElement('div');
  div.textContent = str;
  return div.innerHTML;
}

function formatMoney(priceValue) {
  const num = parseFloat(priceValue);
  if (isNaN(num)) return '';
  return num.toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL'
  });
}

function debounce(fn, ms) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn(...args), ms);
  };
}

function buildProductSearchUrl(term) {
  return `/search?q=${encodeURIComponent(term)}&type=product&options%5Bprefix%5D=last`;
}

function ensureProductSearchFields(form) {
  if (!form) return;

  [
    ['type', 'product'],
    ['options[prefix]', 'last']
  ].forEach(([name, value]) => {
    if (form.querySelector(`[name="${name}"]`)) return;

    const input = document.createElement('input');
    input.type = 'hidden';
    input.name = name;
    input.value = value;
    form.appendChild(input);
  });
}

/* ================================================================
   DROPDOWN LEGACY (mantido para compatibilidade)
   ================================================================ */

function renderRecentSearches(container, emptyState, countEl) {
  const searches = getRecentSearches();

  if (countEl) {
    countEl.textContent = searches.length;
    countEl.style.display = searches.length > 0 ? 'inline-flex' : 'none';
  }

  if (searches.length === 0) {
    container.innerHTML = '';
    emptyState.classList.remove('hidden');
    return;
  }

  emptyState.classList.add('hidden');
  container.innerHTML = searches.map(term => `
    <a
      href="${buildProductSearchUrl(term)}"
      class="inline-flex items-center gap-1.5 px-3 py-1.5 bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm rounded-md transition"
    >
      <svg class="w-3.5 h-3.5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/>
      </svg>
      <span class="truncate max-w-40">${escapeHtml(term)}</span>
    </a>
  `).join('');
}

let abortController = null;

async function fetchPredictiveSearch(term) {
  if (abortController) abortController.abort();
  abortController = new AbortController();

  const url = `/search/suggest.json?q=${encodeURIComponent(term)}&resources[type]=product&resources[limit]=10&resources[options][unavailable_products]=last`;

  try {
    const res = await fetch(url, { signal: abortController.signal });
    if (!res.ok) throw new Error('Erro na busca');
    const data = await res.json();
    return data.resources?.results?.products || [];
  } catch (err) {
    if (err.name === 'AbortError') return null;
    console.error('Predictive search error:', err);
    return [];
  }
}

function renderPredictiveResults(products, container, emptyState, viewAllLink, term) {
  if (!products || products.length === 0) {
    container.innerHTML = '';
    emptyState.classList.remove('hidden');
    if (viewAllLink) viewAllLink.classList.add('hidden');
    return;
  }

  emptyState.classList.add('hidden');
  if (viewAllLink) {
    viewAllLink.href = buildProductSearchUrl(term);
    viewAllLink.classList.remove('hidden');
  }

  container.innerHTML = products.map(product => {
    const image = product.image
      ? `<img src="${escapeHtml(product.image)}" alt="${escapeHtml(product.title)}" class="w-16 aspect-product object-cover rounded-md flex-shrink-0" loading="lazy" width="64">`
      : `<div class="w-16 aspect-product bg-gray-100 rounded-md flex items-center justify-center text-gray-400 flex-shrink-0"><svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"/></svg></div>`;

    const price = product.price !== undefined
      ? `<span class="text-sm font-medium text-gray-900">${formatMoney(product.price)}</span>`
      : '';

    return `
      <a
        href="${escapeHtml(product.url)}"
        class="flex items-center gap-3 p-2 rounded-lg hover:bg-gray-50 transition"
      >
        ${image}
        <div class="flex-1 min-w-0">
          <p class="text-sm font-medium text-gray-900 truncate">${escapeHtml(product.title)}</p>
          ${price ? `<p class="text-sm text-gray-600">${price}</p>` : ''}
        </div>
      </a>
    `;
  }).join('');
}

function initSearchDropdown(wrapper) {
  const input = wrapper.querySelector('input[type="search"]');
  const dropdown = wrapper.querySelector('[data-search-dropdown]');
  if (!input || !dropdown) return;

  const recentPanel = dropdown.querySelector('[data-recent-searches-panel]');
  const predictivePanel = dropdown.querySelector('[data-predictive-panel]');
  const recentContainer = dropdown.querySelector('[data-recent-searches-container]');
  const recentEmpty = dropdown.querySelector('[data-empty-state]');
  const countEl = dropdown.querySelector('[data-recent-count]');
  const predictiveContainer = dropdown.querySelector('[data-predictive-results]');
  const predictiveEmpty = dropdown.querySelector('[data-predictive-empty]');
  const viewAllLink = dropdown.querySelector('[data-view-all-link]');

  function showRecent() {
    recentPanel.classList.remove('hidden');
    predictivePanel.classList.add('hidden');
    renderRecentSearches(recentContainer, recentEmpty, countEl);
  }

  function showPredictive() {
    recentPanel.classList.add('hidden');
    predictivePanel.classList.remove('hidden');
  }

  function showDropdown() {
    dropdown.classList.remove('hidden');
  }

  function hideDropdown() {
    setTimeout(() => {
      if (!dropdown.matches(':hover') && document.activeElement !== input) {
        dropdown.classList.add('hidden');
      }
    }, 150);
  }

  input.addEventListener('focus', () => {
    const term = input.value.trim();
    if (term.length === 0) showRecent();
    showDropdown();
  });

  input.addEventListener('blur', hideDropdown);
  dropdown.addEventListener('mouseleave', hideDropdown);

  const runPredictive = debounce(async (term) => {
    if (term.length === 0) {
      showRecent();
      return;
    }

    showPredictive();
    predictiveContainer.innerHTML = `
      <div class="flex items-center justify-center py-4">
        <svg class="animate-spin h-5 w-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
          <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
      </div>
    `;
    predictiveEmpty.classList.add('hidden');
    if (viewAllLink) viewAllLink.classList.add('hidden');

    const products = await fetchPredictiveSearch(term);
    if (products === null) return;
    renderPredictiveResults(products, predictiveContainer, predictiveEmpty, viewAllLink, term);
  }, 250);

  input.addEventListener('input', (e) => {
    runPredictive(e.target.value.trim());
  });

  const form = wrapper.closest('form');
  if (form) {
    form.addEventListener('submit', (e) => {
      ensureProductSearchFields(form);
      const term = input.value.trim();
      if (term) addRecentSearch(term);
    });
  }
}

function initSearchForm(form) {
  if (form.closest('[data-search-dropdown-wrapper]')) return;

  form.addEventListener('submit', (e) => {
    ensureProductSearchFields(form);
    const input = form.querySelector('input[name="q"]');
    if (input) {
      const term = input.value.trim();
      if (term) addRecentSearch(term);
    }
  });
}

/* ================================================================
   SEARCH OVERLAY (full-screen com animação)
   ================================================================ */

function renderOverlayRecentSearches(container, emptyState, countEl) {
  const searches = getRecentSearches();

  if (countEl) {
    countEl.textContent = searches.length;
    countEl.style.display = searches.length > 0 ? 'inline-flex' : 'none';
  }

  if (searches.length === 0) {
    container.innerHTML = '';
    emptyState.classList.remove('hidden');
    return;
  }

  emptyState.classList.add('hidden');
  container.innerHTML = searches.map(term => `
    <a
      href="${buildProductSearchUrl(term)}"
      class="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white hover:bg-gray-100 text-gray-700 text-sm rounded-md transition border border-gray-200"
    >
      <svg class="w-3.5 h-3.5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/>
      </svg>
      <span class="truncate max-w-52">${escapeHtml(term)}</span>
    </a>
  `).join('');
}

function renderOverlayProductGrid(products, container, emptyState) {
  if (!products || products.length === 0) {
    container.innerHTML = '';
    emptyState.classList.remove('hidden');
    return;
  }

  emptyState.classList.add('hidden');
  container.innerHTML = products.map(product => {
    const image = product.image
      ? `<img src="${escapeHtml(product.image)}" alt="${escapeHtml(product.title)}" class="w-full h-full object-cover transition-transform duration-300 group-hover:scale-[1.03]" loading="lazy" width="300" height="400">`
      : `<div class="w-full h-full flex items-center justify-center text-gray-400"><svg class="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"/></svg></div>`;

    const price = product.price !== undefined
      ? `<span class="text-3xs font-semibold text-gray-900">${formatMoney(product.price)}</span>`
      : '';

    return `
      <a href="${escapeHtml(product.url)}" class="block group" data-overlay-product-link>
        <div class="aspect-product bg-gray-200 rounded-lg overflow-hidden mb-3">
          ${image}
        </div>
        <p class="text-3xs font-medium text-gray-900 leading-tight line-clamp-2 mb-1">${escapeHtml(product.title)}</p>
        ${price}
      </a>
    `;
  }).join('');
}

function initSearchOverlay() {
  const overlay = document.querySelector('[data-search-overlay]');
  if (!overlay) return;

  const input = overlay.querySelector('[data-search-overlay-input]');
  const clearBtn = overlay.querySelector('[data-search-overlay-clear]');
  const closeBtn = overlay.querySelector('[data-search-overlay-close]');
  const backdrop = overlay.querySelector('[data-search-overlay-backdrop]');
  const viewAllLink = overlay.querySelector('[data-overlay-view-all]');

  const recentPanel = overlay.querySelector('[data-overlay-recent-panel]');
  const recentContainer = overlay.querySelector('[data-overlay-recent-container]');
  const recentEmpty = overlay.querySelector('[data-overlay-recent-empty]');
  const recentCount = overlay.querySelector('[data-overlay-recent-count]');

  const resultsPanel = overlay.querySelector('[data-overlay-results-panel]');
  const resultsGrid = overlay.querySelector('[data-overlay-results-grid]');
  const resultsEmpty = overlay.querySelector('[data-overlay-results-empty]');

  function openOverlay() {
    overlay.classList.add('is-open');
    overlay.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';

    // Renderiza buscas recentes
    const term = input.value.trim();
    if (term.length === 0) {
      showRecent();
    } else {
      showResults();
    }

    // Foca no input após a animação
    requestAnimationFrame(() => {
      setTimeout(() => input.focus(), 50);
    });
  }

  function closeOverlay() {
    overlay.classList.remove('is-open');
    overlay.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
    input.blur();
  }

  function showRecent() {
    recentPanel.classList.remove('hidden');
    resultsPanel.classList.add('hidden');
    renderOverlayRecentSearches(recentContainer, recentEmpty, recentCount);
    if (clearBtn) clearBtn.classList.add('invisible', 'opacity-0');
  }

  function showResults() {
    recentPanel.classList.add('hidden');
    resultsPanel.classList.remove('hidden');
    if (clearBtn) clearBtn.classList.remove('invisible', 'opacity-0');
  }

  function showSkeleton() {
    resultsGrid.innerHTML = Array.from({ length: 5 }).map(() => '<div class="search-overlay-skeleton-item"></div>').join('');
    resultsEmpty.classList.add('hidden');
  }

  // Abrir via toggles
  document.querySelectorAll('[data-search-overlay-toggle]').forEach(btn => {
    btn.addEventListener('click', openOverlay);
  });

  // Fechar
  closeBtn.addEventListener('click', closeOverlay);
  backdrop.addEventListener('click', closeOverlay);

  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && overlay.classList.contains('is-open')) {
      closeOverlay();
    }
  });

  // Limpar input
  clearBtn.addEventListener('click', () => {
    input.value = '';
    showRecent();
    input.focus();
  });

  // Live search
  const runOverlaySearch = debounce(async (term) => {
    if (term.length === 0) {
      showRecent();
      return;
    }

    showResults();
    showSkeleton();
    if (viewAllLink) {
      viewAllLink.href = buildProductSearchUrl(term);
    }

    const products = await fetchPredictiveSearch(term);
    if (products === null) return; // abortado
    renderOverlayProductGrid(products, resultsGrid, resultsEmpty);
  }, 250);

  input.addEventListener('input', (e) => {
    runOverlaySearch(e.target.value.trim());
  });

  // Salvar busca recente no submit (Enter)
  input.addEventListener('keydown', (e) => {
    if (e.key === 'Enter') {
      const term = input.value.trim();
      if (term) {
        addRecentSearch(term);
        window.location.href = buildProductSearchUrl(term);
      }
    }
  });

  // Salvar busca ao clicar em produto do overlay
  overlay.addEventListener('click', (e) => {
    const productLink = e.target.closest('[data-overlay-product-link]');
    if (productLink) {
      const term = input.value.trim();
      if (term) addRecentSearch(term);
      return;
    }

    const recentLink = e.target.closest('[data-overlay-recent-container] a');
    if (recentLink) {
      // Não salva nada, apenas navega para busca recente
      return;
    }
  });
}

/* ================================================================
   INICIALIZAÇÃO
   ================================================================ */

function initAllSearch() {
  document.querySelectorAll('[data-search-dropdown-wrapper]').forEach(initSearchDropdown);
  document.querySelectorAll('form[action*="/search"]').forEach(initSearchForm);
  initSearchOverlay();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initAllSearch);
} else {
  initAllSearch();
}
